import { BalloonText } from '@/components/BalloonText'
import CollaboratorSelector from '@/components/CollaboratorSelector/CollaboratorSelector'
import { MuseLogo } from '@/components/MuseLogo'
import AddFriendIcon from '@/CustomIcons/AddFriendIcon'
import CollaboratorsIcon from '@/CustomIcons/CollaboratorsIcon'
import CopyIcon from '@/CustomIcons/CopyIcon'
import { getListenOnlySessionLink, getSessionJoinLink } from '@/Logic'
import { useRoomContext } from '@/RoomContext'
import { useCommunityFeature } from '@/services/useCommunityFeature'
import useRootSnackbar from '@/services/useRootSnackbar'
import { Button } from '@/UIComponents2/Button'
import { Checkbox } from '@/UIComponents2/Checkbox'
import { ControlButton } from '@/UIComponents2/ControlButton'
import { useUserContext } from '@/UserContext'
import { copyToClipboard, isGuestUsername } from '@/utils'
import { cn } from '@/utlis/cn'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutate } from 'restful-react'

export function Invite() {
    const { t } = useTranslation()
    return (
        <div className="flex h-full w-full flex-col justify-begin items-between py-4 gap-4">
            <div className="pl-[20px] pt-[5px]">
                <MuseLogo className="w-[92px]" />
            </div>
            <div className="self-center max-w-[500px] px-4">
                <div className="t-title">{t('Invite Users to Session')}</div>
                <InviteCollaborators className="pt-[32px] max-w-[600px]" />
            </div>
        </div>
    )
}

type InviteCollaboratorsProps = {
    className?: string
    onSent?: () => void
}
export function InviteCollaborators(props: InviteCollaboratorsProps) {
    const { t } = useTranslation()
    const [roomState, roomDispatch] = useRoomContext()

    const isLivestream = roomState.isListeningSession ?? false

    const allowUserAutoJoin = roomState.isSubroomsEnabled ? true : (roomState.allowUserAutoJoin ?? false)

    const isSubroom = !!roomState.parentRoom
    const disableAutoJoinCheckbox = isSubroom || roomState.isSubroomsEnabled

    // NOTE: there's a copy of this in `useRoomActions`. It's probably best to refactor
    // the ops to it.
    const { mutate: updateRoomApi, loading } = useMutate({
        verb: 'PATCH',
        path: `/v1/rooms/${roomState.roomUrn || roomState.preRoomUrn || ''}`,
    })

    const onAllowUserAutoJoinToggle = () => {
        if (loading) {
            return
        }

        const allowUserAutoJoinNext = !allowUserAutoJoin

        roomDispatch({
            type: 'update_room_context',
            value: { allowUserAutoJoin: allowUserAutoJoinNext },
        })
        updateRoomApi({ allowUserAutoJoin: allowUserAutoJoinNext })
    }

    return (
        <div className={cn('flex flex-col items-center min-w-0 min-h-0', props.className)}>
            <div className="flex w-full flex-col items-start gap-6">
                {/*  Participant Session Link */}
                <ParticipantSessionLink
                    title={t('Participant Session Link')}
                    joinLink={getSessionJoinLink(roomState)}
                    obsecure={isLivestream}
                />

                {/*  Audience Session Link */}
                {isLivestream && (
                    <ParticipantSessionLink
                        title={t('Audience Session Link')}
                        joinLink={getListenOnlySessionLink(roomState)}
                        isStreamLink
                    />
                )}

                <CollaboratorInvite onSent={props.onSent} />

                {roomState.isHost && (
                    <Checkbox
                        checked={!allowUserAutoJoin}
                        label={t(`Enable waiting room`) || ''}
                        onChange={onAllowUserAutoJoinToggle}
                        disabled={disableAutoJoinCheckbox}
                    />
                )}
            </div>
        </div>
    )
}

type ParticipantSessionLinkProps = {
    title: string
    joinLink: string
    isStreamLink?: boolean
    obsecure?: boolean
}
function ParticipantSessionLink(props: ParticipantSessionLinkProps) {
    const { t } = useTranslation()
    const [copied, setCopied] = useState(false)
    const [obsecured, setObsecured] = useState(props.obsecure)

    const handleCopy = () => {
        copyToClipboard(props.joinLink)
        setCopied(true)
        setTimeout(() => {
            setCopied(false)
        }, 700)
    }

    return (
        <div className="flex w-full flex-col gap-[8px]">
            <div className="flex items-center gap-[8px] pl-1">
                {props.isStreamLink ? (
                    <CollaboratorsIcon className="text-muse-yellow" style={{ fontSize: '1.3rem' }} />
                ) : (
                    <AddFriendIcon withGradient style={{ fontSize: '1.3rem' }} />
                )}
                <span className="t-base-medium">{props.title}</span>
            </div>
            <div className="grid w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-[8px]">
                <div
                    className="relative group overflow-hidden rounded-[12px] border-muse-toplit px-[17px] py-[7px] shadow-glow bg-white/3"
                    onMouseLeave={() => props.obsecure && setObsecured(true)}
                >
                    <p
                        className={cn(
                            'truncate bg-gradient-to-r from-[#FCE640] to-[#F49629] bg-clip-text text-transparent transition-[blur_500ms]',
                            obsecured && 'blur-[5px]'
                        )}
                    >
                        {props.joinLink}
                    </p>
                    {obsecured && (
                        <Button
                            onClick={() => setObsecured(false)}
                            className="hidden group-hover:flex absolute inset-0 items-center justify-center"
                        >
                            Show Link
                        </Button>
                    )}
                </div>
                <ControlButton variant="primary" className="relative" onClick={() => handleCopy()}>
                    <CopyIcon className="text-black" />
                    <BalloonText active={copied} text={t('Copied!')} />
                </ControlButton>
            </div>
        </div>
    )
}

type CollaboratorInviteProps = {
    onSent?: () => void
}
function CollaboratorInvite(props: CollaboratorInviteProps) {
    const { t } = useTranslation()
    const [userState] = useUserContext()
    const [roomState] = useRoomContext()
    const community = useCommunityFeature()
    const snackbar = useRootSnackbar()

    const handleInvite = async (users: string[]) => {
        const roomUrn = roomState.roomUrn || roomState.preRoomUrn
        if (roomUrn) {
            const result = await community.inviteToSessionCall({ userUrns: users, roomUrn })
            if (result && result.status === 'ok') {
                handleSendInvitationResultSuccess(result)
                props.onSent?.()
            } else {
                handleSendInvitationResultError(result)
            }
        } else {
            handleSendInvitationResultError(false)
        }
    }

    const handleSendInvitationResultSuccess = (result: any) => {
        let { addedUsernames, notFoundUsernames } = result

        if (!addedUsernames) {
            addedUsernames = []
        }
        if (!notFoundUsernames) {
            notFoundUsernames = []
        }

        if (notFoundUsernames.length > 0) {
            snackbar.show({
                severity: 'warning',
                message: `Sent invitation! (Failed to send for ${notFoundUsernames.length} user(s).)`,
            })
        } else {
            snackbar.show({
                severity: 'success',
                message: t('Sent invitation!'),
            })
        }
    }

    const handleSendInvitationResultError = (_result: any) => {
        snackbar.show({
            severity: 'error',
            message: t('There was an error while trying to send the invitation.') || '',
        })
    }

    const suggestedCollaborators = useMemo(() => {
        let users = [...(userState.profile?.knownUsers ?? [])]
        users = users.filter((u) => !isGuestUsername(u.username))
        users.reverse()
        return users
    }, [userState.profile])

    return (
        <CollaboratorSelector
            onSubmit={handleInvite}
            label={t('Invite by Username')}
            suggestions={suggestedCollaborators.map((u) => u.username)}
        />
    )
}
